import { useState, useEffect } from 'react';
import { Trash2, Landmark, Plus } from 'lucide-react';
import Modal from '../components/Modal';
import { useToast } from '../context/ToastContext';

function IntegrationsScreen() {
  const { addToast } = useToast();

  // Load bank accounts from localStorage or initialize with defaults
  const [bankAccounts, setBankAccounts] = useState(() => {
    const saved = localStorage.getItem('linked_bank_accounts');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Failed to parse bank accounts", e);
      }
    }
    return [
      { id: 1, bankName: "Emirates NBD", accountName: "Promotional LLC-FZ", accountNumber: "10123456789", status: "Connected" },
      { id: 2, bankName: "ADCB", accountName: "Promotional LLC-FZ", accountNumber: "20987654321", status: "Primary" }
    ];
  });

  // Persist bank accounts when updated
  useEffect(() => {
    localStorage.setItem('linked_bank_accounts', JSON.stringify(bankAccounts));
  }, [bankAccounts]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    bankName: '',
    accountName: '',
    accountNumber: '',
    status: 'Connected'
  });

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleLinkBankClick = () => {
    setFormData({
      bankName: '',
      accountName: '',
      accountNumber: '',
      status: 'Connected'
    });
    setIsModalOpen(true);
  };

  const handleLinkBankSubmit = (e) => {
    e.preventDefault();
    if (!formData.bankName || !formData.accountName || !formData.accountNumber) {
      addToast("Please fill out all fields", "error");
      return;
    }

    const newBank = {
      id: Date.now(),
      bankName: formData.bankName.trim(),
      accountName: formData.accountName.trim(),
      accountNumber: formData.accountNumber.trim(),
      status: formData.status
    };

    // If new bank is set to Primary, downgrade previous Primary accounts to Connected
    if (newBank.status === 'Primary') {
      setBankAccounts(prev => [
        ...prev.map(b => b.status === 'Primary' ? { ...b, status: 'Connected' } : b),
        newBank
      ]);
    } else {
      setBankAccounts(prev => [...prev, newBank]);
    }

    setIsModalOpen(false);
    addToast(`${newBank.bankName} has been linked successfully!`, "success");
  };

  const handleDeleteBank = (id, bankName) => {
    setBankAccounts(prev => prev.filter(b => b.id !== id));
    addToast(`${bankName} account has been unlinked successfully.`, "success");
  };

  return (
    <div className="crm-screen">
      <div className="screen-header">
        <div className="header-info">
          <h1>Integrations & Settings</h1>
          <p>Manage secure bank account links</p>
        </div>
      </div>

      <div className="marketing-grid">
        <div className="marketing-left" style={{ gridColumn: 'span 2' }}>
          <div className="marketing-card">
            <div className="card-header" style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Landmark size={20} color="var(--primary-color)" />
                <h2>Linked Bank Accounts</h2>
              </div>
              <button 
                className="primary-btn" 
                style={{ padding: '8px 16px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}
                onClick={handleLinkBankClick}
              >
                <Plus size={16} /> Link New Bank
              </button>
            </div>
            <div className="card-body">
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Bank Name</th>
                      <th>Account Name</th>
                      <th>Account Number</th>
                      <th>Status</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bankAccounts.map(bank => (
                      <tr key={bank.id}>
                        <td className="font-bold">{bank.bankName}</td>
                        <td>{bank.accountName}</td>
                        <td>{bank.accountNumber}</td>
                        <td>
                          <span className={`status-pill ${bank.status.toLowerCase()}`}>
                            {bank.status}
                          </span>
                        </td>
                        <td className="text-right">
                          <button 
                            className="icon-btn danger" 
                            title="Unlink Bank" 
                            onClick={() => handleDeleteBank(bank.id, bank.bankName)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {bankAccounts.length === 0 && (
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                          No linked bank accounts. Click "Link New Bank" to add one.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Link New Bank Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Link New Bank Account"
        footer={
          <>
            <button className="secondary-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
            <button className="primary-btn" onClick={handleLinkBankSubmit}>Link Account</button>
          </>
        }
      >
        <form className="modal-form" onSubmit={handleLinkBankSubmit}>
          <div className="form-group">
            <label>Bank Name</label>
            <input 
              type="text" 
              name="bankName" 
              value={formData.bankName} 
              onChange={handleChange} 
              placeholder="e.g. Emirates NBD, ADCB, Mashreq" 
              required 
            />
          </div>
          <div className="form-group">
            <label>Account Holder Name</label>
            <input 
              type="text" 
              name="accountName" 
              value={formData.accountName} 
              onChange={handleChange} 
              placeholder="e.g. Company LLC" 
              required 
            />
          </div>
          <div className="form-group">
            <label>Account Number / IBAN</label>
            <input 
              type="text" 
              name="accountNumber" 
              value={formData.accountNumber} 
              onChange={handleChange} 
              placeholder="e.g. AE830000000000000000000" 
              required 
            />
          </div>
          <div className="form-group">
            <label>Link Status</label>
            <select name="status" value={formData.status} onChange={handleChange}>
              <option value="Connected">Connected</option>
              <option value="Primary">Primary</option>
            </select>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default IntegrationsScreen;
